import {Vue, Component, Prop, PropSync, Provide, ProvideReactive, Watch} from 'vue-property-decorator';
import DatePickerComponent from "@/app/shared/components/date-picker.component.vue";
import IndieInput from "@/app/shared/components/indie-input.vue";
import { debounceTime, pluck, switchMap } from "rxjs/operators";
import { from } from "rxjs";
import { Inject } from "inversify-props";
import { BookingService } from "../../services/booking.service";
import Itinerary from "../../shared/models/itinerary.model";
import { Validator } from "vee-validate";
import { State } from "../../shared/models/state.model";
import { Country } from "../../shared/models/country.model";
import BillingForm from "../../shared/models/billing-form.model";
import { passengerTypes, BAGGAGE_FEE_PER_FLIGHT } from "../../shared/common/constants";
import Toasted from 'vue-toasted';

Vue.use(Toasted);

@Component<PassengersComponent>({
  components: ({
    DatePickerComponent,
    IndieInput
  }),
  subscriptions() {
    return {
      country: this.$fromDOMEvent(".country", "keyup").pipe(
        debounceTime(300),
        pluck<Event, string>("target", "value"),
        switchMap(value => from(this.searchCountry(value)))
      ),
      state: this.$fromDOMEvent(".state", "keyup").pipe(
        debounceTime(300),
        pluck<Event, string>("target", "value"),
        switchMap(value => from(this.searchState(value, this.billingForm.country)))
      ),
      city: this.$fromDOMEvent(".city", "keyup").pipe(
        debounceTime(300),
        pluck<Event, string>("target", "value"),
        switchMap(value => from(this.searchCity(value, this.billingForm.state)))
      )
    };
  }
})
export default class PassengersComponent extends Vue {
  @Inject() private readonly bookingService: BookingService;

  @Prop() private propsPersons: any;
  @Prop() private propsIsValidForm: number;
  @Prop() private propsBillingForm: BillingForm;
  @Prop() private propsCurrency: any;
  @Prop() private propsTotalCost: number;
  @Prop() private propsSelectDate: Function;
  @Prop() private propsChangeBilling: Function;
  @Prop() private propsCityList: any;
  @Prop() private propsSearchInProcess: any;
  @Prop() private propsSearchCity: Function;
  @Prop() private propsOnCityChange: Function;
  @Prop() private propsStateList: any;
  @Prop() private propsCountryList: any;
  @Prop() private propsNextStep: Function;
  @Prop() private propsOnChangeNameSurname: Function;
  @Prop() private propsCountCost: Function;
  @Prop() private propsPriceCoeficient: number;

  public persons: any = this.propsPersons;
  public isValidForm: any = this.propsIsValidForm;
  public billingForm = this.propsBillingForm;
  public cityList = this.propsCityList;
  public searchInProcess = this.propsSearchInProcess;
  public stateList = this.propsStateList;
  public countryList = this.propsCountryList;
  public cityListIsEmpty: any = false;
  public countryListIsEmpty: any = false;
  public stateListIsEmpty: any = false;
  public baggagePrice: string = '';
  public selectDate = this.propsSelectDate;
  public changeBilling = this.propsChangeBilling;
  public onCityChange = this.propsOnCityChange;
  public nextStep = this.propsNextStep;
  public onChangeNameSurname = this.propsOnChangeNameSurname;
  public passengerTypes: any = passengerTypes;
  public readonly BAGGAGE_FEE_PER_FLIGHT = BAGGAGE_FEE_PER_FLIGHT;
  public readonly formNameSectionId = "form-names-cnt";
  public readonly toastsData: any = {
    fieldsIncomplete: {name: "fields_incomplete", message: "Please fill out all required fields", options: {duration: 5000, singleton: true, type: "error"}}
  };

  private currency: { code: '', sign: '', rate: 0};


  constructor() {
    super();
    this.persons = this.propsPersons;
    this.isValidForm = this.propsIsValidForm;
    this.billingForm = this.propsBillingForm;
    this.currency = this.propsCurrency;
    this.baggagePrice = (BAGGAGE_FEE_PER_FLIGHT * this.currency.rate).toFixed();
  }

  mounted() {
    this.registerToasts();
  }

  public registerToasts(){
    this.$toasted.register(this.toastsData.fieldsIncomplete.name, this.toastsData.fieldsIncomplete.message, this.toastsData.fieldsIncomplete.options);
  }

  public countCost(index: number) {
    this.propsCountCost(index);
  }

  public searchCountry(value: string) {
    this.cityList = [];
    this.searchInProcess = true;
    return this.bookingService
      .getCountryList({
        parent: "",
        query: value
      })
      .then(res => {
        this.countryList = [];
        this.countryListIsEmpty = res.data.response.length < 1;
        if (res.data.response.length > 50) {
          for (let i = 0; i < 50; i++) {
            this.countryList.push(res.data.response[i]);
          }
        } else {
          for (let i = 0; i < res.data.response.length; i++) {
            this.countryList.push(res.data.response[i]);
          }
        }
        this.searchInProcess = false;
        this.countryList.sort((a: Country, b: Country) =>
          a.value > b.value ? 1 : b.value > a.value ? -1 : 0
        );
      });
  }

  public searchState(value: string, parent: string) {
    this.cityList = [];
    this.searchInProcess = true;
    return this.bookingService
      .getStateList({
        parent: parent,
        query: value
      })
      .then(res => {
        this.stateList = [];
        this.stateListIsEmpty = res.data.response.length < 1;
        if (res.data.response.length > 50) {
          for (let i = 0; i < 50; i++) {
            this.stateList.push(res.data.response[i]);
          }
        } else {
          for (let i = 0; i < res.data.response.length; i++) {
            this.stateList.push(res.data.response[i]);
          }
        }
        this.searchInProcess = false;
        this.stateList.sort((a: State, b: State) =>
          a.value > b.value ? 1 : b.value > a.value ? -1 : 0
        );
      });
  }

  public searchCity(value: string, parent: string) {
    this.cityList = [];
    this.searchInProcess = true;
    return this.bookingService
      .getCityList({
        parent: parent,
        query: value
      })
      .then(res => {
        this.cityList = [];
        this.cityListIsEmpty = res.data.response.length < 1;
        if (res.data.response.length > 50) {
          for (let i = 0; i < 50; i++) {
            this.cityList.push(res.data.response[i]);
          }
        } else {
          for (let i = 0; i < res.data.response.length; i++) {
            this.cityList.push(res.data.response[i]);
          }
        }
        this.searchInProcess = false;
        this.cityList.sort((a: Itinerary, b: Itinerary) =>
          a.city > b.city ? 1 : b.city > a.city ? -1 : 0
        );
      });
  }

  private async validate() {
    await this.$validator.validate()
      .then(valid => {
        this.isValidForm = valid && this.billingForm.country && this.billingForm.state && this.billingForm.city;

        if (valid) {
          this.nextStep(1, "tab-flights");
        } else {
          this.handleFormError();
        }
      }).catch(() => {
        this.handleFormError();
      });
  }

  private handleFormError(){
    this.$toasted.global[this.toastsData.fieldsIncomplete.name]();
    let container: HTMLElement|null = this.$el.querySelector(`#${this.formNameSectionId}`);
    if(container){
      let offsetTop = container.offsetTop;
      //container.scrollIntoView(true);
      window.scrollTo(0, container.offsetTop + 120);
    }
  }
}
